import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCreateGroup } from '../../../hooks/admin/useGroups';
import type { GroupCreateRequest } from '../../../types/admin';

export default function GroupCreate() {
  const navigate = useNavigate();
  const createGroup = useCreateGroup();

  const { register, handleSubmit, formState: { errors } } = useForm<GroupCreateRequest>({
    defaultValues: {
      locationName: '',
      latitude: 0,
      longitude: 0,
      description: '',
    },
  });

  const onSubmit = async (data: GroupCreateRequest) => {
    try {
      await createGroup.mutateAsync(data);
      navigate('/admin/groups');
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Create New Group</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Group Name *</label>
            <input
              type="text"
              {...register('locationName', { required: 'Group name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Enter group name"
            />
            {errors.locationName && (
              <p className="text-red-600 text-sm mt-1">{errors.locationName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Latitude *</label>
              <input
                type="number"
                step="any"
                {...register('latitude', {
                  required: 'Latitude is required',
                  min: { value: -90, message: 'Must be between -90 and 90' },
                  max: { value: 90, message: 'Must be between -90 and 90' },
                  valueAsNumber: true,
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., 43.6532"
              />
              {errors.latitude && (
                <p className="text-red-600 text-sm mt-1">{errors.latitude.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Longitude *</label>
              <input
                type="number"
                step="any"
                {...register('longitude', {
                  required: 'Longitude is required',
                  min: { value: -180, message: 'Must be between -180 and 180' },
                  max: { value: 180, message: 'Must be between -180 and 180' },
                  valueAsNumber: true,
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., -79.3832"
              />
              {errors.longitude && (
                <p className="text-red-600 text-sm mt-1">{errors.longitude.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Optional notes about this group"
            />
          </div>

          <div className="flex gap-4 justify-end">
            <button
              type="submit"
              disabled={createGroup.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createGroup.isPending ? 'Creating...' : 'Create Group'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/groups')}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
